import { ReactNode, useMemo, useRef, useEffect, useState } from "react";
import { Box, Button, Skeleton } from "@mui/material";
import CustomSwitch from "global/components/CustomSwitch/CustomSwitch";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import styles from "../AiWebForm.styles";
import { versionOrigin, withThankYouShown } from "../AiWebFormHelper";
import { AiDesign } from "../AiWebFormTypesAndValidation";
import { MonitorGlyph, PhoneGlyph, RestoreGlyph } from "./AiWebFormIcons";
import { AiSelectionPrompt } from "./AiSelectionPrompt";
import { getSelectionAndRect, SelectionInfo } from "../AiPreviewPromptHelper";

const copy = strings.aiWebForm;

interface Props {
  /** What the preview shows: the version being previewed, else the current one. */
  design: AiDesign | null;
  /** A version other than the current one is on screen. */
  previewing: boolean;
  /** Phone layout: no Desktop/Phone switch, the page fills the width. */
  compact: boolean;
  device: "desktop" | "phone";
  onDevice: (device: "desktop" | "phone") => void;
  showThankYou: boolean;
  onShowThankYou: (value: boolean) => void;
  onBackToCurrent: () => void;
  onRestore: (version: number) => void;
  canEdit: boolean;
  /** The versions overlay, drawn over the preview from the right. */
  overlay?: ReactNode;
  /** Too narrow beside the open versions panel: its selected row carries the same actions. */
  hideBanner?: boolean;
  /** Called when user submits an instruction from the selection prompt. */
  onPromptSubmit?: (message: string) => void;
  /** Whether the prompt should be disabled (turn running, old version, no permission, etc.) */
  promptDisabled?: boolean;
}

/**
 * The page as signers will see it, in a sandboxed iframe: no scripts and no
 * same-origin access, so the HTML (the AI's, unedited) cannot reach the app.
 * The iframe has a fixed height and scrolls on its own; scripts being off, it
 * cannot size itself to its content.
 */
export const AiPreviewPanel = ({
  design,
  previewing,
  compact,
  device,
  onDevice,
  showThankYou,
  onShowThankYou,
  onBackToCurrent,
  onRestore,
  canEdit,
  overlay,
  hideBanner,
  onPromptSubmit,
  promptDisabled = false,
}: Props) => {
  const html = design?.html;
  const srcDoc = useMemo(() => (html ? (showThankYou ? withThankYouShown(html) : html) : ""), [html, showThankYou]);
  // With the thank-you page shown the line names it instead of where the version came from (07).
  const meta = design
    ? [fillCopy(copy.versionButton, { version: design.version }), showThankYou ? copy.thankYouPage : versionOrigin(design)]
        .filter(Boolean)
        .join(" · ")
    : copy.building;
  const phone = !compact && device === "phone";

  const bannerShown = !!(previewing && design && !hideBanner);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo | null>(null);
  const selectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Clear selection whenever device, layout, or document changes
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
      selectionTimeoutRef.current = null;
    }
    setSelectionInfo(null);

    const iframe = iframeRef.current;
    if (!iframe) return;

    let attachedDoc: Document | null = null;
    let attachedWin: Window | null = null;
    const isMouseDownRef = { current: false };

    const updateSelection = () => {
      const currentIframe = iframeRef.current;
      if (!currentIframe) return;
      const info = getSelectionAndRect(currentIframe);
      if (info && info.text) {
        setSelectionInfo(info);
      } else {
        setSelectionInfo(null);
      }
    };

    const handleMouseUp = () => {
      isMouseDownRef.current = false;
      if (selectionTimeoutRef.current) clearTimeout(selectionTimeoutRef.current);
      selectionTimeoutRef.current = setTimeout(() => {
        updateSelection();
      }, 15);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (["Shift", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
        if (selectionTimeoutRef.current) clearTimeout(selectionTimeoutRef.current);
        selectionTimeoutRef.current = setTimeout(() => {
          updateSelection();
        }, 15);
      }
    };

    const handleSelectionChange = () => {
      if (isMouseDownRef.current) return;
      // If user is focused inside the prompt dialog, do not dismiss
      if (document.activeElement?.closest?.('[role="dialog"]')) return;
      updateSelection();
    };

    const handleScrollOrResize = () => {
      const currentIframe = iframeRef.current;
      if (!currentIframe) return;
      const info = getSelectionAndRect(currentIframe);
      if (info && info.text) {
        setSelectionInfo(info);
      } else {
        setSelectionInfo(null);
      }
    };

    const handleDocMouseDown = () => {
      isMouseDownRef.current = true;
      if (selectionTimeoutRef.current) clearTimeout(selectionTimeoutRef.current);
      setSelectionInfo(null);
    };

    const handleWindowMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && !target.closest?.('[role="dialog"]') && target !== iframeRef.current) {
        if (selectionTimeoutRef.current) clearTimeout(selectionTimeoutRef.current);
        setSelectionInfo(null);
      }
    };

    const detachCurrent = () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
        selectionTimeoutRef.current = null;
      }
      if (attachedDoc) {
        try {
          attachedDoc.removeEventListener("mouseup", handleMouseUp);
          attachedDoc.removeEventListener("touchend", handleMouseUp);
          attachedDoc.removeEventListener("keyup", handleKeyUp as any);
          attachedDoc.removeEventListener("mousedown", handleDocMouseDown as any);
          attachedDoc.removeEventListener("selectionchange", handleSelectionChange);
        } catch {}
        attachedDoc = null;
      }
      if (attachedWin) {
        try {
          attachedWin.removeEventListener("scroll", handleScrollOrResize);
        } catch {}
        attachedWin = null;
      }
    };

    const attach = () => {
      const currentIframe = iframeRef.current;
      if (!currentIframe) return;
      try {
        const doc = currentIframe.contentDocument;
        const win = currentIframe.contentWindow;
        if (!doc || !doc.body) return;

        if (attachedDoc === doc) return;

        detachCurrent();

        attachedDoc = doc;
        attachedWin = win;

        doc.addEventListener("mouseup", handleMouseUp);
        doc.addEventListener("touchend", handleMouseUp);
        doc.addEventListener("keyup", handleKeyUp as any);
        doc.addEventListener("mousedown", handleDocMouseDown as any);
        doc.addEventListener("selectionchange", handleSelectionChange);

        if (win) {
          win.addEventListener("scroll", handleScrollOrResize, { passive: true });
        }
      } catch {
        // Cross-origin restriction guard
      }
    };

    // Attach immediately if doc is ready
    attach();

    const onLoad = () => attach();
    iframe.addEventListener("load", onLoad);

    // Poll for the first 1 second to ensure document attachment after srcDoc parses
    let pollCount = 0;
    const pollId = setInterval(() => {
      attach();
      pollCount++;
      if (pollCount > 20) clearInterval(pollId);
    }, 50);

    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mousedown", handleWindowMouseDown);
    window.addEventListener("resize", handleScrollOrResize, { passive: true });

    const canvasEl = canvasRef.current;
    if (canvasEl) {
      canvasEl.addEventListener("scroll", handleScrollOrResize, { passive: true });
    }

    return () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
        selectionTimeoutRef.current = null;
      }
      clearInterval(pollId);
      iframe.removeEventListener("load", onLoad);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mousedown", handleWindowMouseDown);
      window.removeEventListener("resize", handleScrollOrResize);
      if (canvasEl) {
        canvasEl.removeEventListener("scroll", handleScrollOrResize);
      }
      detachCurrent();
    };
  }, [srcDoc, device, compact]);

  const handlePromptSubmit = (instruction: string) => {
    if (!onPromptSubmit) return;
    onPromptSubmit(instruction.trim());
    setSelectionInfo(null);
  };

  const frame = html ? (
    <Box component="iframe" ref={iframeRef} title={copy.previewTitle} sandbox="allow-same-origin" srcDoc={srcDoc} sx={styles.iframe} />
  ) : (
    <Skeleton variant="rectangular" sx={{ flex: 1, height: "100%", borderRadius: "12px" }} animation="wave" />
  );

  return (
    <Box sx={styles.preview} component="section" aria-label={copy.preview}>
      <Box sx={styles.previewHeader}>
        <Box sx={styles.previewHeading}>
          <Box sx={styles.previewTitle}>{copy.preview}</Box>
          <Box sx={styles.previewMeta}>{meta}</Box>
        </Box>
        <Box sx={styles.previewSpacer} />
        {!compact && (
          <>
            <Box sx={styles.segmented} role="group" aria-label={copy.previewSize}>
              <Box
                component="button"
                type="button"
                sx={styles.segment(device === "desktop")}
                aria-pressed={device === "desktop"}
                aria-label={copy.desktop}
                onClick={() => {
                  setSelectionInfo(null);
                  onDevice("desktop");
                }}
              >
                <MonitorGlyph />
                <Box component="span" sx={styles.segmentLabel}>
                  {copy.desktop}
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                sx={styles.segment(device === "phone")}
                aria-pressed={device === "phone"}
                aria-label={copy.phone}
                onClick={() => {
                  setSelectionInfo(null);
                  onDevice("phone");
                }}
              >
                <PhoneGlyph />
                <Box component="span" sx={styles.segmentLabel}>
                  {copy.phone}
                </Box>
              </Box>
            </Box>
            <Box sx={styles.headerRule} />
          </>
        )}
        <Box component="label" sx={styles.thankYou}>
          <CustomSwitch
            name="aiwf-thank-you"
            checked={showThankYou}
            onChange={(_event, checked) => onShowThankYou(checked)}
          />
          {copy.showThankYou}
        </Box>
      </Box>

      {bannerShown && design && (
        <Box sx={styles.previewingBanner(!!overlay)} role="status">
          <Box sx={styles.bannerLabel}>
            {fillCopy(copy.previewingBanner, { version: design.version, origin: versionOrigin(design) })}
          </Box>
          <Box component="button" type="button" sx={styles.bannerGhost} onClick={onBackToCurrent}>
            {copy.backToCurrent}
          </Box>
          {canEdit && (
            <Button size="small" variant="contained" sx={styles.smallButton} startIcon={<RestoreGlyph />} onClick={() => onRestore(design.version)}>
              {fillCopy(copy.restoreVersion, { version: design.version })}
            </Button>
          )}
        </Box>
      )}

      <Box ref={canvasRef} sx={styles.canvas(bannerShown)}>
        {phone ? (
          <Box sx={styles.phoneFrame}>
            <Box sx={styles.phoneScreen}>
              <Box sx={styles.phoneStatus} aria-hidden="true">
                <span>9:41</span>
                <Box sx={styles.phoneNotch} />
                <span>100%</span>
              </Box>
              {frame}
            </Box>
          </Box>
        ) : (
          <Box sx={styles.desktopFrame}>{frame}</Box>
        )}
      </Box>
      {selectionInfo && !promptDisabled && onPromptSubmit ? (
        <AiSelectionPrompt
          anchorRect={selectionInfo.rect}
          iframeRect={selectionInfo.iframeRect}
          selectedText={selectionInfo.text}
          onSubmit={handlePromptSubmit}
          disabled={promptDisabled}
          onClose={() => setSelectionInfo(null)}
        />
      ) : null}
      {overlay}
    </Box>
  );
};
